/**
 * Unified error handling utility
 * Provides consistent error handling across the application
 */

import { message } from 'antd';
import { AxiosError } from 'axios';

export interface ApiError {
  status?: number;
  message?: string;
  detail?: string;
  errors?: unknown;
}

// Axios error type for API errors
export type ApiErrorType = AxiosError<{
  detail?: string | Array<{ loc?: string[]; msg?: string }>;
  message?: string;
}>;

/**
 * Extract error message from unknown type error
 */
export const getErrorMessageFromUnknown = (error: unknown): string => {
  // Axios error with response
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { status?: number; data?: unknown } };
    const { status, data } = err.response || {};

    // Pydantic validation error
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((err) => {
          const e = err as { loc?: string[]; msg?: string };
          const field = e.loc?.join('.') || 'unknown';
          return `${field}: ${e.msg}`;
        });
        return messages.join('; ');
      }
      return typeof detail === 'string' ? detail : JSON.stringify(detail);
    }

    // HTTP status code messages
    if (status) {
      return getStatusMessage(status);
    }
  }

  // Network error
  if (error && typeof error === 'object' && 'request' in error) {
    return '网络连接失败，请检查网络设置';
  }

  // Standard Error
  if (error instanceof Error) {
    return error.message;
  }

  return '未知错误，请稍后重试';
};

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error: unknown): string => {
  // Axios error with response
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { status?: number; data?: unknown } };
    const { status, data } = err.response || {};

    // Pydantic validation error
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      if (Array.isArray(detail)) {
        const messages = detail.map((err) => {
          const e = err as { loc?: string[]; msg?: string };
          const field = e.loc?.join('.') || 'unknown';
          return `${field}: ${e.msg}`;
        });
        return messages.join('; ');
      }
      return typeof detail === 'string' ? detail : JSON.stringify(detail);
    }

    // HTTP status code messages
    if (status) {
      return getStatusMessage(status);
    }
  }

  // Network error
  if (error && typeof error === 'object' && 'request' in error) {
    return '网络连接失败，请检查网络设置';
  }

  // Standard Error
  if (error instanceof Error) {
    return error.message;
  }

  return '未知错误，请稍后重试';
};

/**
 * Get user-friendly message for HTTP status codes
 */
export const getStatusMessage = (status: number): string => {
  const statusMessages: Record<number, string> = {
    400: '请求参数错误',
    401: '未登录或登录已过期，请重新登录',
    403: '无权限访问此资源',
    404: '请求的资源不存在',
    409: '资源冲突，请刷新后重试',
    422: '数据验证失败',
    429: '请求过于频繁，请稍后再试',
    500: '服务器内部错误',
    502: '网关错误',
    503: '服务暂时不可用',
    504: '网关超时',
  };
  
  return statusMessages[status] || `请求失败 (${status})`;
};

/**
 * Handle API error with appropriate user feedback
 */
export const handleApiError = (error: unknown, customMessages?: Partial<Record<number, string>>): void => {
  const status = error && typeof error === 'object' && 'response' in error
    ? (error as { response?: { status?: number } }).response?.status
    : undefined;

  // Check for custom message
  if (status && customMessages && customMessages[status]) {
    message.error(customMessages[status]);
    return;
  }

  // Get error message
  const errorMessage = getErrorMessage(error);

  // Show error message
  if (status === 401) {
    message.error(errorMessage);
    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  } else {
    message.error(errorMessage);
  }
};

/**
 * Handle validation errors from backend
 */
export const handleValidationError = (error: unknown): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { data?: { detail?: Array<{ loc?: string[]; msg?: string }> } } };
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      detail.forEach((e) => {
        const field = e.loc?.[e.loc.length - 1] || 'unknown';
        fieldErrors[field] = e.msg || '';
      });
    }
  }

  return fieldErrors;
};

/**
 * Show success message
 */
export const showSuccess = (msg: string = '操作成功'): void => {
  message.success(msg);
};

/**
 * Show error message
 */
export const showError = (msg: string = '操作失败'): void => {
  message.error(msg);
};

/**
 * Show warning message
 */
export const showWarning = (msg: string): void => {
  message.warning(msg);
};

/**
 * Show info message
 */
export const showInfo = (msg: string): void => {
  message.info(msg);
};

/**
 * Error handler for async operations
 * Usage: await asyncOperation().catch(asyncErrorHandler('操作失败'))
 */
export const asyncErrorHandler = (defaultMessage?: string) => (error: unknown) => {
  handleApiError(error);
  if (defaultMessage) {
    console.error(defaultMessage, error);
  }
  throw error; // Re-throw to allow caller to handle if needed
};

/**
 * Wrap async function with error handling
 */
export const withErrorHandling = <T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  errorMessage?: string
): T => {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error);
      if (errorMessage) {
        console.error(errorMessage, error);
      }
      throw error;
    }
  }) as T;
};

/**
 * Check if error is a specific HTTP status
 */
export const isErrorStatus = (error: unknown, status: number): boolean => {
  return error && typeof error === 'object' && 'response' in error
    ? (error as { response?: { status?: number } }).response?.status === status
    : false;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: unknown): boolean => {
  return isErrorStatus(error, 422) || isErrorStatus(error, 400);
};

/**
 * Check if error is an Ant Design Form validation error (has errorFields)
 */
export const isFormValidationError = (error: unknown): boolean => {
  return error !== null && typeof error === 'object' && 'errorFields' in error;
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  return isErrorStatus(error, 401);
};

/**
 * Check if error is a permission error
 */
export const isPermissionError = (error: unknown): boolean => {
  return isErrorStatus(error, 403);
};

/**
 * Check if error is a not found error
 */
export const isNotFoundError = (error: unknown): boolean => {
  return isErrorStatus(error, 404);
};

/**
 * Format validation errors for Ant Design Form
 */
export const formatFormErrors = (error: unknown): Array<{ name: string; errors: string[] }> => {
  const formErrors: Array<{ name: string; errors: string[] }> = [];

  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { data?: { detail?: Array<{ loc?: string[]; msg?: string }> } } };
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      detail.forEach((e) => {
        const fieldName = e.loc?.[e.loc.length - 1];
        if (fieldName) {
          formErrors.push({
            name: fieldName,
            errors: [e.msg || ''],
          });
        }
      });
    }
  }

  return formErrors;
};

const errorHandler = {
  getErrorMessage,
  getErrorMessageFromUnknown,
  getStatusMessage,
  handleApiError,
  handleValidationError,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  asyncErrorHandler,
  withErrorHandling,
  isErrorStatus,
  isValidationError,
  isAuthError,
  isPermissionError,
  isNotFoundError,
  formatFormErrors,
};

export default errorHandler;
